import moment from "moment";
import { bffApi, type NeedDto } from "../../autogen/bff-api";
import { lastDayOfPeriod } from "../contractPeriod";
import type { ToolContext, ToolResult } from "./tools";
import { hiddenEventDataOf } from "./wizardHiddenFields";

export const CREATED_FROM_NEED = "createdFromNeed";

const START_DATE_PATH = "eventData.contractDuration.startDate";
const OFFERED_MONTHS = [12, 24, 36, 48];

type Row = Record<string, unknown>;

export const withOriginatingNeed = async (
  result: ToolResult,
  eventId: unknown,
  ctx: ToolContext,
  now: Date = new Date(),
): Promise<ToolResult> => {
  if (!result.ok || !isRow(result.data) || typeof eventId !== "string" || !eventId) return result;
  const need = await needForProcurement(eventId, ctx);
  if (need === NO_NEED) return result;
  if (need === UNREADABLE) {
    if (hiddenEventDataOf(result.data).includes("contractDuration")) return result;
    return { ok: true, data: { ...result.data, [CREATED_FROM_NEED]: UNREADABLE_NEED } };
  }
  return { ok: true, data: { ...result.data, [CREATED_FROM_NEED]: createdFromNeed(need, result.data, now) } };
};

const NO_NEED = "none";
const UNREADABLE = "unreadable";
const NOT_SHOWN_TO_USER = [403, 404];

export const UNREADABLE_NEED: Row = {
  unreadable: true,
  contractPeriod:
    "Systemet fikk ikke sjekket om anskaffelsen er opprettet fra et behov. Si at du ikke fikk sjekket behovet, at det " +
    "kan hjelpe å prøve igjen, og spør brukeren når kontrakten skal starte. Ta aldri startdatoen fra en dato i " +
    "anskaffelsens tittel.",
};

const needForProcurement = async (
  sourcingEventId: string,
  ctx: ToolContext,
): Promise<NeedDto | typeof NO_NEED | typeof UNREADABLE> => {
  try {
    const found = await ctx.dispatch(bffApi.endpoints.getNeedForProcurement.initiate({ sourcingEventId })).unwrap();
    return found.need ?? NO_NEED;
  } catch (error) {
    const status = isRow(error) ? error.status : undefined;
    return typeof status === "number" && NOT_SHOWN_TO_USER.includes(status) ? NO_NEED : UNREADABLE;
  }
};

export const createdFromNeed = (need: NeedDto, event: unknown, now: Date): Row => {
  const title = need.title?.trim() || undefined;
  const named = { needId: need.id, ...(title ? { title } : {}) };
  if (hiddenEventDataOf(event).includes("contractDuration")) return named;
  const deliveryDate = isoDay(need.deliveryDate);
  const periodMonths = typeof need.periodMonths === "number" && need.periodMonths > 0 ? need.periodMonths : undefined;
  const startsFromNeed = !!deliveryDate && deliveryDate >= osloToday(now);
  return {
    ...named,
    ...(deliveryDate ? { deliveryDate } : {}),
    ...(periodMonths ? { periodMonths } : {}),
    contractPeriod: contractPeriodNote(deliveryDate, periodMonths, osloToday(now)),
    ...(startsFromNeed
      ? {
          lastContractDayByMonths: lastContractDayByMonths(deliveryDate, periodMonths),
          startDateSource: { path: START_DATE_PATH, source: { kind: "NEED", ...named } },
        }
      : {}),
  };
};

const lastContractDayByMonths = (start: string, periodMonths: number | undefined): Record<string, string> =>
  Object.fromEntries(
    [...new Set([...OFFERED_MONTHS, ...(periodMonths ? [periodMonths] : [])])]
      .sort((a, b) => a - b)
      .map((months) => [String(months), lastDayOfPeriod(moment(start, "YYYY-MM-DD"), months).format("YYYY-MM-DD")]),
  );

const contractPeriodNote = (deliveryDate: string | undefined, periodMonths: number | undefined, today: string): string => {
  const length = periodMonths
    ? `«[anbefalt] » foran «${periodMonths} måneder», behovets «Periode», som et eget alternativ om det ikke er blant dem`
    : "«[anbefalt] » foran «24 måneder», siden behovet ikke har noen «Periode»";
  const askLength =
    "Spør «Hvor lenge skal kontrakten løpe?» med en valgblokk med «12 måneder», «24 måneder», «36 måneder» og " +
    `«48 måneder», og ${length}, før du foreslår et kort med kontraktsperioden.`;
  const noTitleDate = "Ta aldri startdatoen fra en dato i anskaffelsens tittel.";
  if (!deliveryDate) {
    return (
      "Anskaffelsen er opprettet fra dette behovet, men behovet har ikke noe ønsket leveringstidspunkt. Foreslå " +
      "ingen startdato: si det, og spør brukeren når kontrakten skal starte. " + askLength + " " + noTitleDate
    );
  }
  if (deliveryDate < today) {
    return (
      `Anskaffelsen er opprettet fra dette behovet, men behovets ønskede leveringstidspunkt ${deliveryDate} har ` +
      "passert. Foreslå ingen startdato: si at datoen har passert, og spør brukeren når kontrakten skal starte. " +
      askLength + " " + noTitleDate
    );
  }
  return (
    `Anskaffelsen er opprettet fra dette behovet. Behovets deliveryDate («Ønsket leveringstidspunkt») ${deliveryDate} ` +
    "er datoen kontrakten skal starte, aldri datoen den slutter: foreslå den som startdato for kontraktsperioden, og " +
    "si at den kommer fra behovet. " + askLength + " Nevner du når kontrakten slutter, før eller etter at du har " +
    "lagret, tar du siste kontraktsdag for den lengden fra lastContractDayByMonths, og regner den aldri ut selv. " +
    "Står ikke lengden der, nevner du ingen sluttdato. Når du lagrer, legger du startDateSource i fieldSources, så " +
    "kortet viser behovet som kilden til startdatoen. " + noTitleDate
  );
};

const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

const isoDay = (value: string | null | undefined): string | undefined =>
  value && ISO_DAY.test(value) ? value.slice(0, 10) : undefined;

const osloToday = (now: Date): string =>
  new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Oslo", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);

const isRow = (value: unknown): value is Row => !!value && typeof value === "object" && !Array.isArray(value);
